import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Spinner,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useApiClient } from '../../apiClient';
import { useSelectedOrganization } from '../organization/hooks/useSelectedOrganization';

interface PayLaterBookingItem {
  paymentIntentId: string;
  userId: string;
  userName: string;
  bookingType: 'court' | 'coaching';
  courtName?: string;
  coachName?: string;
  coachId?: string;
  startTime?: string;
  endTime?: string;
  amount: number;
  lastChargedAmount?: number;
  currency: string;
  status: string;
  sportType?: string;
}

const PAY_LATER_COACHING_QUERY_KEY = 'pay-later-coaching-list';

function daysSince(dateStr?: string): number | null {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

const PayLaterCoachingListPage: React.FC = () => {
  const apiClient = useApiClient();
  const toast = useToast();
  const { selectedOrganization } = useSelectedOrganization();
  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedItem, setSelectedItem] = useState<PayLaterBookingItem | null>(null);
  const [chargeAmount, setChargeAmount] = useState(0);

  const { data, isLoading } = useQuery<PayLaterBookingItem[]>({
    queryKey: [PAY_LATER_COACHING_QUERY_KEY, selectedOrganization?.id],
    queryFn: () =>
      apiClient.fetchApi<PayLaterBookingItem[], never>({
        method: 'GET',
        endpoint: `payments/pay-later-bookings?organizationId=${selectedOrganization?.id}&charged=false&limit=200`,
      }),
    enabled: !!selectedOrganization?.id,
  });

  const chargeMutation = useMutation({
    mutationFn: async ({ piId, amount }: { piId: string; amount: number }) =>
      apiClient.fetchApi<any, any>({
        endpoint: `payments/charge-pay-later-booking/${piId}`,
        method: 'POST',
        body: { customAmount: amount },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PAY_LATER_COACHING_QUERY_KEY] });
      toast({ title: 'Paiement encaissé avec succès', status: 'success', duration: 3000 });
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: 'Échec du paiement',
        description: error?.message || 'Une erreur est survenue lors du paiement',
        status: 'error',
        duration: 5000,
      });
    },
  });

  const coachingItems = data?.filter((item) => item.bookingType === 'coaching') ?? [];
  const staleCount = coachingItems.filter(
    (item) => item.endTime && daysSince(item.endTime)! > 14,
  ).length;

  const handleCharge = (item: PayLaterBookingItem) => {
    setSelectedItem(item);
    setChargeAmount(item.amount / 100);
    onOpen();
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="200px">
        <Spinner size="lg" />
      </Flex>
    );
  }

  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="md">Sessions de coaching - Paiement differe</Heading>
        <HStack spacing={3}>
          <Tag colorScheme="blue" size="lg">
            {coachingItems.length} en attente
          </Tag>
          {staleCount > 0 && (
            <Tag colorScheme="orange" size="lg">
              {staleCount} stale (&gt;14j)
            </Tag>
          )}
        </HStack>
      </Flex>

      {coachingItems.length === 0 ? (
        <Card>
          <CardBody>
            <Text textAlign="center" color="gray.500" py={8}>
              Aucune session en attente de paiement. Bien joue !
            </Text>
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardBody p={0}>
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Joueur</Th>
                    <Th>Coach</Th>
                    <Th>Date session</Th>
                    <Th>Jours depuis</Th>
                    <Th isNumeric>Montant</Th>
                    <Th>Statut</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {coachingItems.map((item) => {
                    const days = item.endTime ? daysSince(item.endTime) : null;
                    return (
                      <Tr key={item.paymentIntentId}>
                        <Td>
                          <Text fontSize="sm" fontWeight="medium">
                            {item.userName}
                          </Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm">{item.coachName || '-'}</Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm">
                            {item.startTime
                              ? new Date(item.startTime).toLocaleDateString('fr-FR', {
                                  day: 'numeric',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : '-'}
                          </Text>
                        </Td>
                        <Td>
                          {days !== null && (
                            <Badge colorScheme={days > 7 ? 'orange' : 'gray'}>
                              {days}j
                            </Badge>
                          )}
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm" fontWeight="medium">
                            {(item.amount / 100).toFixed(2)} {item.currency.toUpperCase()}
                          </Text>
                        </Td>
                        <Td>
                          <Badge colorScheme="blue">En attente</Badge>
                        </Td>
                        <Td>
                          <Button
                            size="xs"
                            colorScheme="blue"
                            onClick={() => handleCharge(item)}
                            isLoading={chargeMutation.isLoading}
                          >
                            Encaisser
                          </Button>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
      )}

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Encaisser la session</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedItem && (
              <>
                <Text mb={1} fontWeight="medium">{selectedItem.userName}</Text>
                <Text mb={1} fontSize="sm" color="gray.600">
                  Coach : {selectedItem.coachName} - {selectedItem.startTime
                    ? new Date(selectedItem.startTime).toLocaleDateString('fr-FR')
                    : ''}
                </Text>
                <Text mb={3} fontSize="sm" color="gray.600">
                  Montant initial : {(selectedItem.amount / 100).toFixed(2)} {selectedItem.currency.toUpperCase()}
                </Text>
                <Text mb={2} fontWeight="medium">Montant a encaisser :</Text>
                <NumberInput
                  value={chargeAmount}
                  onChange={(_, val) => setChargeAmount(val || 0)}
                  min={0.01}
                  max={selectedItem.amount / 100}
                  precision={2}
                  step={0.5}
                >
                  <NumberInputField />
                </NumberInput>
                <Alert status="warning" mt={4} borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">
                    Cette action debitera la carte du joueur sans action de sa part.
                    Assurez-vous que la session a bien ete dispensee.
                  </Text>
                </Alert>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Annuler
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => {
                if (selectedItem) {
                  chargeMutation.mutate({
                    piId: selectedItem.paymentIntentId,
                    amount: Math.round(chargeAmount * 100),
                  });
                }
              }}
              isLoading={chargeMutation.isLoading}
            >
              Encaisser {chargeAmount.toFixed(2)} {selectedItem?.currency?.toUpperCase() || 'EUR'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PayLaterCoachingListPage;
